<!-- src/components/LogViewer.svelte -->
<script>
    import { onMount, onDestroy } from "svelte";
    import logger from "../utils/logger";

    // Props with default values
    export let showTimestamp = true;
    export let maxLogs = 100;
    export let autoScroll = true;
    export let visible = false; // Whether the log viewer is visible

    // Internal state
    let logs = [];
    let logContainer;
    let filterLevel = "ALL";

    // Function to load logs
    function loadLogs() {
        if (localStorage) {
            logs = logger.getStoredLogs().slice(-maxLogs);
        }
    }

    // Toggle visibility
    function toggleVisibility() {
        visible = !visible;
        if (visible) {
            loadLogs();
        }
    }

    // Apply filter by log level
    function applyFilter(level) {
        filterLevel = level;
    }

    // Get CSS class for log level
    function getLogLevelClass(level) {
        switch (level) {
            case "DEBUG":
                return "log-debug";
            case "INFO":
                return "log-info";
            case "WARN":
                return "log-warn";
            case "ERROR":
                return "log-error";
            default:
                return "";
        }
    }

    // Clear all logs
    function clearLogs() {
        logger.clearStoredLogs();
        logs = [];
    }

    // Download logs as JSON file
    function downloadLogs() {
        const data = JSON.stringify(logs, null, 2);
        const blob = new Blob([data], { type: "application/json" });
        const url = URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;
        a.download = `app-logs-${new Date().toISOString()}.json`;
        document.body.appendChild(a);
        a.click();

        // Clean up
        setTimeout(() => {
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        }, 100);
    }

    // Enable localStorage logging on mount
    onMount(() => {
        logger.setShowTimestamp(showTimestamp);
        logger.setStoreLogsInLocalStorage(true);
        if (visible) {
            loadLogs();
        }

        // Add keyboard shortcut (Ctrl+Shift+L) to toggle log viewer
        // const handleKeyDown = (e) => {
        //     if (e.ctrlKey && e.shiftKey && e.key === "L") {
        //         toggleVisibility();
        //     }
        // };

        window.addEventListener("keydown", handleKeyDown);

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    });

    // Filter the logs based on selected level
    $: filteredLogs =
        filterLevel === "ALL"
            ? logs
            : logs.filter((log) => log.level === filterLevel);

    // Automatically scroll to the bottom when logs update
    $: if (autoScroll && logContainer && visible && filteredLogs.length > 0) {
        setTimeout(() => {
            logContainer.scrollTop = logContainer.scrollHeight;
        }, 0);
    }
</script>

{#if visible}
    <div class="log-viewer">
        <div class="log-header">
            <h3>Application Logs</h3>
            <div class="log-controls">
                <select
                    bind:value={filterLevel}
                    on:change={() => applyFilter(filterLevel)}
                >
                    <option value="ALL">All Levels</option>
                    <option value="DEBUG">Debug</option>
                    <option value="INFO">Info</option>
                    <option value="WARN">Warning</option>
                    <option value="ERROR">Error</option>
                </select>

                <button on:click={clearLogs}>Clear</button>
                <button on:click={downloadLogs}>Download</button>
                <button on:click={toggleVisibility}>Close</button>
            </div>
        </div>

        <div class="log-container" bind:this={logContainer}>
            {#if filteredLogs.length === 0}
                <div class="log-empty">No logs to display</div>
            {:else}
                {#each filteredLogs as log}
                    <div class={`log-entry ${getLogLevelClass(log.level)}`}>
                        {#if showTimestamp}
                            <span class="log-timestamp"
                                >{new Date(
                                    log.timestamp,
                                ).toLocaleTimeString()}</span
                            >
                        {/if}
                        <span class="log-level">[{log.level}]</span>
                        <span class="log-message">{log.message}</span>
                        {#if log.data}
                            <pre class="log-data">{JSON.stringify(
                                    log.data,
                                    null,
                                    2,
                                )}</pre>
                        {/if}
                    </div>
                {/each}
            {/if}
        </div>
    </div>
{:else}
    <button
        class="log-button"
        on:click={toggleVisibility}
        on:keydown={(e) => e.key === "Enter" && toggleVisibility()}
        aria-label="Show application logs"
    >
        Show Logs
    </button>
{/if}

<style>
    .log-viewer {
        position: fixed;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 300px;
        background-color: #1e1e1e;
        color: #e0e0e0;
        font-family: monospace;
        z-index: 9999;
        display: flex;
        flex-direction: column;
        border-top: 1px solid #444;
    }

    .log-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 5px 10px;
        background-color: #333;
        border-bottom: 1px solid #444;
    }

    .log-header h3 {
        margin: 0;
        font-size: 14px;
    }

    .log-controls {
        display: flex;
        gap: 5px;
    }

    .log-controls button,
    .log-controls select {
        background-color: #444;
        color: #e0e0e0;
        border: 1px solid #555;
        padding: 3px 8px;
        font-size: 12px;
        cursor: pointer;
        border-radius: 3px;
    }

    .log-container {
        flex: 1;
        overflow-y: auto;
        padding: 5px 10px;
        font-size: 12px;
    }

    .log-entry {
        padding: 2px 0;
        border-bottom: 1px solid #333;
        white-space: pre-wrap;
        word-break: break-all;
    }

    .log-timestamp {
        color: #888;
        margin-right: 5px;
    }

    .log-level {
        font-weight: bold;
        margin-right: 5px;
    }

    .log-debug {
        color: #8c9eff;
    }
    .log-info {
        color: #80cbc4;
    }
    .log-warn {
        color: #ffcc80;
    }
    .log-error {
        color: #ef9a9a;
    }

    .log-data {
        margin: 5px 0 5px 15px;
        padding: 5px;
        background-color: #2d2d2d;
        border-radius: 3px;
        font-size: 11px;
        overflow-x: auto;
    }

    .log-button {
        position: fixed;
        bottom: 10px;
        right: 10px;
        background-color: #333;
        color: #e0e0e0;
        padding: 5px 10px;
        border-radius: 5px;
        font-size: 12px;
        cursor: pointer;
        border: 1px solid #444;
        z-index: 9999;
    }

    .log-empty {
        color: #888;
        font-style: italic;
        text-align: center;
        padding: 20px;
    }
</style>
